// ============================================================================
// dataTypes/liveStreamManagment/sourceRegistry/acquirers/JingleAcquirer.ts
// Acquiert un flux vidéo+audio depuis un fichier (data: URL ou HTTP).
//
// Le jingle est lu dans un <video> caché. Le flux est capturé via
// captureStream() pour produire un MediaStream injectable dans le
// pipeline de composition comme n'importe quelle autre source.
//
// Quand la lecture se termine (sauf si loop=true), le registry est
// notifié pour remettre la source en 'idle'.
// ============================================================================

import { Source, SourceAcquirer, JingleSourceConfig } from '../types';
import { captureMediaElement, stopMediaElementCapture } from './mediaElementCapture';
import { setVideoSource, releaseVideoSource, isHlsUrl } from '../../utils_videoSource';

interface JingleEntry {
  video:      HTMLVideoElement;
  objectUrl?: string;     // si on a converti un data: en blob URL
}

export class JingleAcquirer implements SourceAcquirer {

  private entries = new Map<string, JingleEntry>();

  /** Callback optionnel — appelé quand le jingle se termine naturellement */
  onEnded?: (sourceId: string) => void;

  async acquire(source: Source): Promise<MediaStream> {
    const config = source.config as JingleSourceConfig;

    // Nettoyage d'une éventuelle entrée précédente
    this.releaseEntry(source.id);

    // Création du <video> caché
    const video = document.createElement('video');
    video.autoplay    = true;
    video.playsInline = true;
    video.muted       = false;    // on veut l'audio du jingle
    video.volume      = config.volume ?? 1;
    video.loop        = config.loop ?? false;
    video.style.display = 'none';
    document.body.appendChild(video);

    const entry: JingleEntry = { video };
    this.entries.set(source.id, entry);

    // Conversion data: → blob URL pour éviter les problèmes de lecture
    let srcUrl: string;
    if (config.url.startsWith('data:')) {
      try {
        const [header, b64] = config.url.split(',');
        const mime   = (header.match(/:(.*?);/) ?? [])[1] ?? 'video/mp4';
        const binary = atob(b64);
        const bytes  = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        const blob   = new Blob([bytes], { type: mime });
        srcUrl            = URL.createObjectURL(blob);
        entry.objectUrl   = srcUrl;
      } catch (err) {
        this.releaseEntry(source.id);
        throw new Error(`Conversion data: → Blob échouée: ${err}`);
      }
    } else {
      srcUrl = config.url;
    }

    // load() réinitialiserait l'élément sous hls.js : seulement pour un fichier.
    setVideoSource(video, srcUrl);
    if (!isHlsUrl(srcUrl)) video.load();

    // Attente du chargement
    const loaded = await new Promise<boolean>((resolve) => {
      const cleanup = () => {
        video.removeEventListener('loadeddata', onReady);
        video.removeEventListener('canplay',    onReady);
        video.removeEventListener('error',      onError);
      };
      const onReady = () => { cleanup(); resolve(true); };
      const onError = () => { cleanup(); resolve(false); };

      if (video.readyState >= 2) { resolve(true); return; }

      video.addEventListener('loadeddata', onReady);
      video.addEventListener('canplay',    onReady);
      video.addEventListener('error',      onError);
      setTimeout(() => { cleanup(); resolve(video.readyState >= 2); }, 15_000);
    });

    if (!loaded) {
      this.releaseEntry(source.id);
      throw new Error(`Impossible de charger le jingle: ${config.url.slice(0, 50)}…`);
    }

    // Lecture
    try {
      await video.play();
    } catch (err) {
      this.releaseEntry(source.id);
      throw new Error(`Lecture du jingle impossible: ${err}`);
    }

    // Notification de fin de lecture
    video.onended = () => {
      if (!video.loop) {
        this.onEnded?.(source.id);
      }
    };

    // captureStream
    // Natif, ou canvas + Web Audio sous Safari (le jingle est déjà non muet).
    const stream = captureMediaElement(video);
    if (!stream) {
      this.releaseEntry(source.id);
      throw new Error('captureStream() non supporté');
    }

    return stream;
  }

  release(source: Source): void {
    this.releaseEntry(source.id);
  }

  private releaseEntry(sourceId: string): void {
    const entry = this.entries.get(sourceId);
    if (!entry) return;
    try {
      entry.video.onended = null;
      releaseVideoSource(entry.video);
      entry.video.pause();
      entry.video.srcObject = null;
      entry.video.src       = '';
      entry.video.load();
      stopMediaElementCapture(entry.video);
      entry.video.remove();
      if (entry.objectUrl) URL.revokeObjectURL(entry.objectUrl);
    } catch { /* best-effort */ }
    this.entries.delete(sourceId);
  }

  /** Accès au <video> element pour le pipeline audio (createMediaElementSource) */
  getVideoElement(sourceId: string): HTMLVideoElement | null {
    return this.entries.get(sourceId)?.video ?? null;
  }
}
